import type { ScenarioStep } from '@/types/eval-studio';

const COMPLIANCE_ACTION_LABELS: Record<string, string> = {
  ALLOW: 'allow',
  BLOCK: 'block',
  AMEND: 'amend',
  BYPASSED: 'bypassed',
  EXPLAIN: 'explain',
  WARN: 'warn',
};

const CHECK_LABELS: Record<string, string> = {
  compliance_result: 'Compliance check',
  agent_selected: 'Expected teammate is selected',
  agent_not_selected: 'Unexpected teammate is not selected',
  handover_occurred: 'Conversation is handed over',
  no_handover: 'Conversation stays with the same teammate',
  reply_count: 'Reply count',
  response_present: 'Teammate sends a response',
  response_contains: 'Response includes the expected text',
  response_not_contains: 'Response avoids the blocked text',
  file_in_response: 'Response includes the expected file',
  output_hygiene: 'Response follows the formatting rules',
  llm_judge: 'AI judge approves the response',
  tool_called: 'Expected tool is used',
  tool_not_called: 'Unexpected tool is not used',
  memory_read: 'Memory is read',
  memory_written: 'Memory is saved',
};

const INTERACTION_LABELS: Record<string, string> = {
  user_message: 'User sends a message',
  thread_reply: 'User replies in a thread',
  file_upload: 'User uploads a file',
  delay: 'Wait before continuing',
  system_event: 'System event is injected',
  compliance_accept: 'User accepts compliance changes',
  compliance_edit: 'User edits the flagged message',
  compliance_bypass: 'User proceeds with the original message',
};

const INTERACTION_SUBTITLES: Record<string, string> = {
  user_message: 'Message to the teammate',
  thread_reply: 'Threaded follow-up message',
  file_upload: 'File attached to the conversation',
  delay: 'Timed pause',
  system_event: 'System-side event',
  compliance_accept: 'Submit the amended text (filter bypassed)',
  compliance_edit: 'Resubmit edited text (re-filtered)',
  compliance_bypass: 'Proceed with original text (filter bypassed)',
};

const OPERATOR_DESCRIPTIONS: Record<string, string> = {
  equals: 'matches exactly',
  contains: 'includes',
  not_contains: 'does not include',
  count_equals: 'equals the expected count',
  matches_pattern: 'matches the expected pattern',
  is_true: 'must happen',
  is_false: 'must not happen',
  any_of: 'matches one of the allowed options',
};

const SCOPE_LABELS: Record<string, string> = {
  since_last_interact: 'Since the last interaction',
  trace_total: 'Across the full scenario run',
  last_response_only: 'Only the latest teammate response',
  since_step_id: 'Since a specific step',
};

const BOOLEAN_CHECK_LABELS: Record<string, { trueLabel: string; falseLabel: string }> = {
  response_present: {
    trueLabel: 'Teammate sends a response',
    falseLabel: 'Teammate does not send a response',
  },
  handover_occurred: {
    trueLabel: 'Conversation is handed over',
    falseLabel: 'Conversation is not handed over',
  },
  no_handover: {
    trueLabel: 'Conversation stays with the same teammate',
    falseLabel: 'Conversation must be handed over',
  },
  llm_judge: {
    trueLabel: 'AI judge approves the response',
    falseLabel: 'AI judge rejects the response',
  },
};

function humanizeToken(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

type CheckStepLike = Pick<ScenarioStep, 'check_type' | 'operator' | 'expected'> & Record<string, unknown>;

function resolveCheckType(step: CheckStepLike): string {
  return String(step.check_type || step.type || 'check');
}

function normalizeScalarExpected(value: unknown, checkType: string): Record<string, unknown> {
  switch (checkType) {
    case 'reply_count':
      return { count: value };
    case 'compliance_result':
      return { assessment: String(value) };
    case 'response_contains':
    case 'response_not_contains':
      return { text: String(value) };
    case 'agent_selected':
    case 'agent_not_selected':
      return typeof value === 'string' ? { agent_id: value } : { agent_id: value };
    case 'file_in_response':
      return typeof value === 'string' ? { mime_type: value } : {};
    case 'tool_called':
    case 'tool_not_called':
      return typeof value === 'string' ? { tool_name: value } : {};
    case 'llm_judge':
      return typeof value === 'string' ? { criteria: value } : {};
    default:
      return {};
  }
}

/** Normalize assert ``expected`` payloads from dicts, scalars, or JSON strings. */
export function resolveExpectedRecord(
  step: CheckStepLike,
  checkType = resolveCheckType(step)
): Record<string, unknown> {
  const raw = step.expected;
  if (raw == null) return {};

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      return normalizeScalarExpected(parsed, checkType);
    } catch {
      return normalizeScalarExpected(trimmed, checkType);
    }
  }

  if (typeof raw === 'number' || typeof raw === 'boolean') {
    return normalizeScalarExpected(raw, checkType);
  }

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }

  return {};
}

function extractReplyCount(step: CheckStepLike): unknown {
  const expected = resolveExpectedRecord(step, 'reply_count');
  const candidates = [expected.count, expected.value, expected.replies, expected.reply_count, step.count];
  for (const candidate of candidates) {
    if (candidate != null && candidate !== '') return candidate;
  }
  return null;
}

function formatReplyCountValue(count: unknown): string | null {
  if (count == null || count === '') return null;
  const text = String(count).trim();
  if (!text) return null;
  const numeric = Number(text);
  if (Number.isFinite(numeric) && /^-?\d+(\.\d+)?$/.test(text)) {
    return String(numeric);
  }
  return humanizeToken(text);
}

function stringifyList(values: unknown): string | null {
  if (!Array.isArray(values) || values.length === 0) return null;
  return values.map((value) => String(value)).join(', ');
}

/** Optional id → display-name map for teammate UUIDs in check labels. */
export type AgentNameById = Record<string, string>;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function looksLikeAgentId(value: string): boolean {
  return UUID_RE.test(value.trim());
}

/** Resolve a teammate id (or raw label) to a human-readable name when possible. */
export function resolveAgentDisplayName(value: string | null | undefined, agentNames?: AgentNameById | null): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (!looksLikeAgentId(raw)) return raw;
  const resolved = agentNames?.[raw] || agentNames?.[raw.toLowerCase()];
  return resolved?.trim() || raw;
}

function formatAgentIdList(values: unknown, agentNames?: AgentNameById | null): string | null {
  if (!Array.isArray(values) || values.length === 0) return null;
  const labels = values.map((value) => resolveAgentDisplayName(String(value), agentNames)).filter(Boolean);
  return labels.length ? labels.join(', ') : null;
}

function formatComplianceAssessmentPhrase(action: string): string {
  const normalized = action.trim().toUpperCase();
  const label = COMPLIANCE_ACTION_LABELS[normalized];
  if (label) {
    if (normalized === 'BYPASSED') {
      return `compliance check should be flagged as ${label}`;
    }
    return `message should be flagged as ${label}`;
  }
  return `message should be flagged as ${humanizeToken(action).toLowerCase()}`;
}

function extractComplianceAssessments(step: CheckStepLike): string[] {
  const expected = resolveExpectedRecord(step, 'compliance_result');
  const fromList = Array.isArray(expected.assessments)
    ? expected.assessments.map((value) => String(value).trim()).filter(Boolean)
    : [];
  if (fromList.length > 0) return fromList;

  const single = expected.assessment ?? expected.action ?? expected.result ?? expected.overall_action;
  if (single != null && String(single).trim()) return [String(single).trim()];

  const stepLevel = step.assessment ?? step.action;
  if (stepLevel != null && String(stepLevel).trim()) return [String(stepLevel).trim()];

  return [];
}

function formatComplianceCheckDisplay(step: CheckStepLike): string {
  const operator = String(step.operator || '');
  const assessments = extractComplianceAssessments(step);
  if (assessments.length === 0) {
    return 'Compliance check (expected action not set — e.g. AMEND, ALLOW, BLOCK)';
  }

  const phrases = assessments.map(formatComplianceAssessmentPhrase);
  if (operator === 'any_of' || assessments.length > 1) {
    const labels = assessments
      .map((action) => COMPLIANCE_ACTION_LABELS[action.trim().toUpperCase()] ?? humanizeToken(action).toLowerCase())
      .join(', ');
    return `Compliance: message should be flagged as one of: ${labels}`;
  }

  return `Compliance: ${phrases[0]}`;
}

/** Compact duration for replay timelines (e.g. chat preview playhead). */
export function formatCompactDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0s';

  const totalMs = Math.round(ms);

  if (totalMs < 1000) {
    return `${totalMs}ms`;
  }

  const totalSeconds = totalMs / 1000;

  if (totalSeconds < 60) {
    if (totalSeconds < 10) {
      const rounded = Math.round(totalSeconds * 10) / 10;
      return Number.isInteger(rounded) ? `${rounded}s` : `${rounded.toFixed(1)}s`;
    }
    return `${Math.round(totalSeconds)}s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds % 60);
  return `${minutes}m ${seconds}s`;
}

function formatDurationMs(value: unknown): string | null {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return `${String(value)} ms`;
  return formatCompactDuration(numeric);
}

function getFileReferenceName(fileReference: unknown): string | null {
  const file = getRecord(fileReference);
  return String(file.name || file.file_name || file.mime_type || file.upload_id || '').trim() || null;
}

// Summarize the files on a step: prefers the ``file_references`` list (N files)
// and falls back to the legacy singular ``file_reference``.
function getFileReferencesSummary(step: Record<string, unknown>): string | null {
  const list = Array.isArray(step.file_references) ? step.file_references : null;
  if (list && list.length) {
    const names = list.map((ref) => getFileReferenceName(ref)).filter(Boolean) as string[];
    if (!names.length) return `${list.length} file(s)`;
    if (names.length === 1) return names[0];
    return `${names.length} files: ${names.join(', ')}`;
  }
  return getFileReferenceName(step.file_reference);
}

function formatReplyCount(count: unknown): string | null {
  const value = formatReplyCountValue(count);
  if (value == null) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  return numeric === 1 ? '1 reply' : `${numeric} replies`;
}

export function formatScenarioCheckLabel(checkType?: string, operator?: string): string {
  const normalizedCheck = checkType || 'check';
  const normalizedOperator = operator || '';
  const booleanLabels = BOOLEAN_CHECK_LABELS[normalizedCheck];

  if (booleanLabels && normalizedOperator === 'is_false') {
    return booleanLabels.falseLabel;
  }
  if (booleanLabels && (normalizedOperator === 'is_true' || !normalizedOperator)) {
    return booleanLabels.trueLabel;
  }

  if (normalizedCheck === 'agent_selected' && normalizedOperator === 'any_of') {
    return 'One of the expected teammates is selected';
  }
  if (normalizedCheck === 'compliance_result') {
    return CHECK_LABELS.compliance_result;
  }

  const baseLabel = CHECK_LABELS[normalizedCheck] ?? humanizeToken(normalizedCheck);
  const operatorDescription = OPERATOR_DESCRIPTIONS[normalizedOperator];
  const skipOperatorSuffix =
    normalizedCheck === 'reply_count' && ['count_equals', 'equals'].includes(normalizedOperator);
  if (skipOperatorSuffix || !operatorDescription || ['equals', 'is_true'].includes(normalizedOperator)) {
    return baseLabel;
  }

  return `${baseLabel} (${operatorDescription})`;
}

export function formatScenarioExpectedValue(step: CheckStepLike, agentNames?: AgentNameById | null): string | null {
  if (step.expected == null) return null;

  const checkType = resolveCheckType(step);
  const expected = resolveExpectedRecord(step, checkType);
  switch (checkType) {
    case 'compliance_result': {
      const assessments = extractComplianceAssessments(step);
      if (assessments.length === 0) return null;
      return assessments
        .map((action) => COMPLIANCE_ACTION_LABELS[action.trim().toUpperCase()] ?? humanizeToken(action).toLowerCase())
        .join(', ');
    }
    case 'agent_selected':
    case 'agent_not_selected': {
      const named = String(expected.agent_name || expected.name || '').trim();
      if (named) return named;
      const fromList =
        formatAgentIdList(expected.agent_ids, agentNames) || formatAgentIdList(expected.agents, agentNames);
      if (fromList) return fromList;
      const single = String(expected.agent_id || expected.agent || '').trim();
      return single ? resolveAgentDisplayName(single, agentNames) : null;
    }
    case 'handover_occurred': {
      const named = String(expected.to_agent_name || '').trim();
      if (named) return named;
      const targetId = String(expected.to_agent_id || '').trim();
      return targetId ? resolveAgentDisplayName(targetId, agentNames) : null;
    }
    case 'reply_count':
      return formatReplyCount(extractReplyCount(step));
    case 'response_contains':
    case 'response_not_contains':
      return expected.text ? `"${String(expected.text)}"` : null;
    case 'file_in_response':
      return String(expected.file_name || expected.mime_type || expected.extension || '').trim() || null;
    case 'output_hygiene': {
      const patterns = stringifyList(expected.forbidden_patterns);
      return patterns ? `Avoid: ${patterns}` : null;
    }
    case 'llm_judge': {
      const criteria = String(expected.criteria || '').trim();
      return criteria ? `"${criteria}"` : null;
    }
    default:
      if (typeof step.expected === 'boolean') return step.expected ? 'Yes' : 'No';
      if (typeof step.expected === 'string' || typeof step.expected === 'number') return String(step.expected);
      if (Object.keys(expected).length === 0) return null;
      return JSON.stringify(step.expected);
  }
}

/**
 * One human-readable line for a scenario check, merging the check type and the
 * concrete expected value (e.g. "Reply count equals 1", not "…matches the
 * expectation (equals the expected count)").
 */
export function formatScenarioCheckDisplay(step: CheckStepLike, agentNames?: AgentNameById | null): string {
  const checkType = resolveCheckType(step);
  const operator = String(step.operator || '');
  const expected = resolveExpectedRecord(step, checkType);

  switch (checkType) {
    case 'reply_count': {
      const count = formatReplyCountValue(extractReplyCount(step));
      if (count != null) return `Reply count equals ${count}`;
      return 'Reply count';
    }
    case 'compliance_result':
      return formatComplianceCheckDisplay(step);
    case 'agent_selected': {
      const target = formatScenarioExpectedValue(step, agentNames);
      if (operator === 'any_of') {
        const agents = target || formatAgentIdList(expected.agent_ids, agentNames);
        if (agents) return `Teammate is one of: ${agents}`;
      }
      if (target) return `Teammate selected: ${target}`;
      break;
    }
    case 'agent_not_selected': {
      const target = formatScenarioExpectedValue(step, agentNames);
      if (operator === 'any_of') {
        const agents = target || formatAgentIdList(expected.agent_ids, agentNames);
        if (agents) return `Teammate is not one of: ${agents}`;
      }
      if (target) return `Teammate not selected: ${target}`;
      break;
    }
    case 'handover_occurred': {
      const target = formatScenarioExpectedValue(step, agentNames);
      if (target) return `Handed over to ${target}`;
      break;
    }
    case 'response_contains':
      if (expected.text != null && String(expected.text).trim()) {
        return `Response includes "${String(expected.text).trim()}"`;
      }
      break;
    case 'response_not_contains':
      if (expected.text != null && String(expected.text).trim()) {
        return `Response avoids "${String(expected.text).trim()}"`;
      }
      break;
    case 'file_in_response': {
      const target = formatScenarioExpectedValue(step, agentNames);
      if (target) return `Response includes file: ${target}`;
      break;
    }
    case 'output_hygiene': {
      const target = formatScenarioExpectedValue(step, agentNames);
      if (target) return target.startsWith('Avoid') ? target : `Output hygiene: ${target}`;
      break;
    }
    case 'tool_called':
    case 'tool_not_called': {
      const tool = String(expected.tool_name || expected.tool || expected.name || '').trim();
      if (tool) {
        return checkType === 'tool_called' ? `Tool called: ${tool}` : `Tool not called: ${tool}`;
      }
      break;
    }
    case 'llm_judge': {
      const criteria = String(expected.criteria || '').trim();
      if (criteria) {
        return operator === 'is_false' ? `AI judge rejects: "${criteria}"` : `AI judge: "${criteria}"`;
      }
      break;
    }
  }

  const label = formatScenarioCheckLabel(checkType, operator);
  const expectedSummary = formatScenarioExpectedValue(step, agentNames);
  if (expectedSummary && !label.toLowerCase().includes(expectedSummary.toLowerCase())) {
    return `${label}: ${expectedSummary}`;
  }
  return label;
}

export function formatScenarioInteractionLabel(type?: string): string {
  const normalizedType = type || 'user_message';
  return INTERACTION_LABELS[normalizedType] ?? humanizeToken(normalizedType);
}

export function formatScenarioInteractionSubtitle(type?: string): string {
  const normalizedType = type || 'user_message';
  return INTERACTION_SUBTITLES[normalizedType] ?? 'Scenario interaction';
}

export function formatScenarioInteractionDetail(
  step: Pick<ScenarioStep, 'content'> & Record<string, unknown>
): string | null {
  const type = String(step.type || step.interact_type || 'user_message');

  if (type === 'delay') {
    return formatDurationMs(step.delay_ms);
  }
  if (type === 'file_upload') {
    return getFileReferencesSummary(step);
  }
  if (step.content != null && String(step.content).trim()) {
    return String(step.content).trim();
  }
  if (step.sent_text != null && String(step.sent_text).trim()) {
    return String(step.sent_text).trim();
  }
  if (step.message != null && String(step.message).trim()) {
    return String(step.message).trim();
  }

  return null;
}

export function formatScenarioOperator(operator?: string): string | null {
  if (!operator) return null;
  return OPERATOR_DESCRIPTIONS[operator] ?? humanizeToken(operator);
}

export function formatScenarioScope(scope?: string): string | null {
  if (!scope) return null;
  return SCOPE_LABELS[scope] ?? humanizeToken(scope);
}
